/** Twilio operational issues — auth, phone provisioning, SMS/WhatsApp/Messenger transport, templates. */
import type { IssueDefinition } from "../issue-definition";
import { ISSUE_CATEGORY, ISSUE_FAULT } from "../issue-enums";
import { META_WHATSAPP_MANAGER_CTA, PHONE_SETTINGS_CTA } from "./ctas";

export const TWILIO_ISSUES = {
  TWILIO_AUTH_FAILED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Twilio credentials are invalid or expired",
    severity: "critical",
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "Twilio connection invalid",
    userGuidance:
      "Your Twilio credentials are invalid or expired, so messages can't be sent. Update them in phone number settings.",
    cta: PHONE_SETTINGS_CTA
  },
  TWILIO_PHONE_NOT_WHATSAPP_ENABLED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Phone number is not enabled for WhatsApp on Twilio",
    severity: "medium",
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "Number not enabled for WhatsApp",
    userGuidance:
      "This phone number isn't enabled for WhatsApp on Twilio. Enable WhatsApp for the number, then retry.",
    cta: META_WHATSAPP_MANAGER_CTA
  },
  TWILIO_SUBACCOUNT_LIMIT_EXCEEDED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Twilio subaccount creation quota exceeded",
    severity: "high",
    fault: ISSUE_FAULT.PROVIDER,
    userTitle: "Twilio capacity limit reached",
    userGuidance:
      "Twilio's account limit was reached while provisioning. This is on the provider side — contact support if it persists."
  },
  TWILIO_PHONE_NOT_AVAILABLE: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Phone number unavailable for purchase on Twilio",
    severity: "medium",
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "Phone number unavailable",
    userGuidance:
      "That phone number is no longer available on Twilio. Pick a different number in phone number settings.",
    cta: PHONE_SETTINGS_CTA
  },
  TWILIO_PHONE_INVALID_CAPABILITY: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Phone number does not support the required capability (SMS/WhatsApp)",
    severity: "medium",
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "Number lacks required capability",
    userGuidance:
      "This number doesn't support the capability this deployment needs (SMS or WhatsApp). Choose a compatible number.",
    cta: PHONE_SETTINGS_CTA
  },
  TWILIO_PHONE_REQUIRES_REGULATORY_ADDRESS: {
    category: ISSUE_CATEGORY.PROVIDER,
    message:
      "Phone number requires a regulatory address — connection has no APPROVED compliance bundle for this country",
    severity: "medium",
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "Regulatory address required",
    userGuidance:
      "This country requires an approved regulatory address before the number can be used. Complete compliance in phone number settings.",
    cta: PHONE_SETTINGS_CTA
  },
  TWILIO_SMS_PUMPING_PROTECTION: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "SMS pumping protection triggered (error code 30450)",
    severity: "high",
    fault: ISSUE_FAULT.PROVIDER,
    userTitle: "SMS blocked by fraud protection",
    userGuidance:
      "Twilio's SMS pumping protection blocked a message it judged high-risk. This is a carrier-side safeguard, not a Nexus error."
  },
  TWILIO_SMS_QUOTA_EXCEEDED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Twilio SMS sending quota exceeded",
    severity: "high",
    fault: ISSUE_FAULT.PROVIDER,
    userTitle: "SMS quota reached",
    userGuidance:
      "Your Twilio SMS sending quota is exhausted. Sending resumes once the quota resets or is raised on Twilio."
  },
  TWILIO_SMS_MESSAGE_SEND_FAILED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "SMS message failed to send via Twilio",
    severity: "high",
    fault: ISSUE_FAULT.PROVIDER,
    userTitle: "SMS failed to send",
    remedy:
      "Read `rawPayload.message` on the row — it is the Twilio error verbatim. `classifySmsError` in " +
      "`apps/backend/src/deployments/adapters/sms/sms-message-sender.adapter.ts` files this code for Twilio " +
      "21211 (invalid phone number), 21614 (not a mobile number), a carrier `undelivered`, or an HTTP 403 " +
      "policy refusal. None of those clears by waiting. There is no re-send queue on this path — that " +
      "adapter's own docblock says the classification picks a dashboard code `and never a retry`, and " +
      "`apps/backend/src/conversations/infrastructure/channel-dispatch/twilio-sms/dispatch-via-twilio-sms.adapter.ts` " +
      "files the same failures as `ProviderRejectedError`, which it logs at error level as needing operator " +
      "attention rather than as the transient class. So the customer's message is gone until somebody sends " +
      "it again: fix the destination number or the policy cause, then tell them to resend. There is nothing " +
      "to replay from here.",
    userGuidance:
      "Twilio refused this SMS, so it was never delivered — and it is not sent again on its own. The " +
      "refusals filed here are the permanent kind rather than a provider blip: the destination is not a " +
      "valid mobile number, the carrier reported it undelivered, or Twilio refused the send on policy. " +
      "Check the number can receive SMS, then send the message again."
  },
  TWILIO_WHATSAPP_MESSAGE_SEND_FAILED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "WhatsApp message failed to send via Twilio",
    severity: "high",
    fault: ISSUE_FAULT.PROVIDER,
    userTitle: "WhatsApp message failed to send",
    remedy:
      "Read `rawPayload.message` on the row — it is the Twilio error verbatim. `classifyWhatsAppError` in " +
      "`apps/backend/src/deployments/adapters/whatsapp/whatsapp-message-sender.adapter.ts` files this code " +
      "for an HTTP 403 policy refusal (its own comment names the session window and content policy) or for a " +
      "number that cannot receive WhatsApp — Twilio 21211, 21614, a carrier `undelivered`, or `not a " +
      "whatsapp`. Neither clears by waiting, and there is no re-send queue on this path: that adapter's " +
      "docblock says the classification picks a dashboard code `and never a retry`. The message is gone " +
      "until somebody sends it again. Confirm the recipient uses WhatsApp on this number: if they do, the " +
      "classifier's other branch is excluded and the refusal was the 403, so read `rawPayload.message` for " +
      "the policy Twilio names before resending.",
    userGuidance:
      "Twilio refused this WhatsApp message, so it was never delivered — and it is not sent again on its " +
      "own. Two causes are filed here: Twilio refused the send on policy, or the number has no WhatsApp " +
      "account. Check the recipient uses WhatsApp on this number, then send the message again."
  },
  TWILIO_WHATSAPP_RATE_LIMIT: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "WhatsApp messaging rate limit exceeded on Twilio",
    severity: "medium",
    fault: ISSUE_FAULT.PROVIDER,
    userTitle: "WhatsApp rate limit reached",
    userGuidance:
      "Twilio is rate-limiting WhatsApp messaging. Throughput recovers on its own once the limit clears, but " +
      "the messages refused while it was in force are not sent again on their own — send those again once " +
      "traffic is flowing."
  },
  TWILIO_MESSENGER_MESSAGE_SEND_FAILED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Messenger message failed to send via Twilio Channels",
    severity: "high",
    fault: ISSUE_FAULT.PROVIDER,
    userTitle: "Messenger message failed to send",
    remedy:
      "`classifyMessengerError` in " +
      "`apps/backend/src/deployments/adapters/messenger/messenger-message-sender.adapter.ts` files this code " +
      "on an HTTP 403 and on nothing else, so every row here is a policy refusal from Twilio Channels rather " +
      "than a transport fault — a transient failure returns `null` from that classifier and is never filed. " +
      "There is no re-send queue on this path, so the message is gone until somebody sends it again. Read " +
      "`rawPayload.message` for the refusal Twilio gave, fix that cause, then have the customer resend.",
    userGuidance:
      "Twilio Channels refused this Messenger message on policy, so it was never delivered — and it is not " +
      "sent again on its own. This code is only ever a policy refusal: the send was rejected outright rather " +
      "than lost in transit. Once the reason for the refusal is resolved, send the message again."
  },
  TWILIO_MESSENGER_RATE_LIMIT: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Messenger messaging rate limit exceeded on Twilio Channels",
    severity: "medium",
    fault: ISSUE_FAULT.PROVIDER,
    userTitle: "Messenger rate limit reached",
    userGuidance:
      "Twilio Channels is rate-limiting Messenger messaging. Throughput recovers on its own once the limit " +
      "clears, but the messages refused while it was in force are not sent again on their own — send those " +
      "again once traffic is flowing."
  },
  TWILIO_TEMPLATE_CREATION_FAILED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "WhatsApp message template creation failed on Twilio",
    severity: "medium",
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "Template creation failed",
    userGuidance:
      "Twilio couldn't create a WhatsApp template. Review the template content and try again from WhatsApp Manager.",
    cta: META_WHATSAPP_MANAGER_CTA
  },
  TWILIO_TEMPLATE_APPROVAL_REJECTED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "WhatsApp message template was rejected by Meta",
    severity: "medium",
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "Template rejected by Meta",
    userGuidance:
      "Meta rejected a WhatsApp template. Edit it to meet Meta's guidelines and resubmit in WhatsApp Manager.",
    cta: META_WHATSAPP_MANAGER_CTA
  },
  TWILIO_PHONE_WEBHOOK_CONFIG_FAILED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Failed to configure webhook on Twilio phone number",
    severity: "low"
  },
  TWILIO_PHONE_RELEASE_FAILED: {
    // Admin-only: the number was already removed from Nexus, but Twilio rejected
    // the release, so it may still be billable until released in the Twilio console.
    category: ISSUE_CATEGORY.BILLING,
    message:
      "Phone number released in Nexus but Twilio rejected the release — number may still be billable, release it manually in the Twilio console",
    severity: "high",
    fault: ISSUE_FAULT.NEXUS
  },
  PHONE_RELEASE_DB_DESYNC: {
    // The inverse of TWILIO_PHONE_RELEASE_FAILED: Twilio released the number
    // (billing stopped) but the Nexus status update failed, so the row may still
    // read ACTIVE and the customer list keeps showing it. Re-running the release
    // is idempotent and will reconcile it.
    category: ISSUE_CATEGORY.BILLING,
    message:
      "Phone number released on Twilio but the Nexus status update failed — the row may still show ACTIVE; re-run the release to reconcile",
    severity: "high",
    fault: ISSUE_FAULT.NEXUS
  },
  PHONE_NUMBER_ORPHANED: {
    // Filed by the phone-number reconciliation cron: an ACTIVE number that is
    // billing with nothing behind it — no active deployment and no WhatsApp
    // sender — past a grace window. Almost always a deployment that was deleted
    // without releasing the number. Review it and release it if truly unused.
    // The cron auto-resolves this issue once the number stops being a candidate
    // (released, reassigned, or a deployment re-attached).
    category: ISSUE_CATEGORY.BILLING,
    message:
      "Phone number is ACTIVE and billing but has no active deployment or WhatsApp sender — review and release it if unused",
    severity: "medium",
    fault: ISSUE_FAULT.NEXUS
  }
} as const satisfies Record<string, IssueDefinition>;
